<script setup lang="ts">
import { useLanguage } from '#composables/useLanguage';
import { C4ButtonBase, C4FunctionalNotice, C4PopUp } from '@c4-links/vue3-links-components';
import {
  DATE_GRANULARITY_DIMENSIONS,
  getTopFlopSortKpi,
  type CountryType,
  type DataTableDimensionsType,
  type DataTableViewType,
  type ExportOptionsType
} from '@carrefour-gcs/shared';
import { computed, nextTick, onMounted, ref } from 'vue';
import { ExportFormatSelector, ExportSuccessNotice } from '.';

const props = defineProps<{
  isOpen: boolean;
  isExportPending: boolean;
  isExportSucceeded: boolean;
  selectedDimensions: DataTableDimensionsType[];
  userCountry?: CountryType;
  dataTableView: DataTableViewType;
}>();
const { t } = useLanguage();

const exportOptions = ref<ExportOptionsType>({
  exportType: 'excel',
  withTopFlopSalesAmount: false,
  withTopFlopSalesQuantity: false
});
const isTopFlopSalesAmountAvailable = ref<boolean>(true);
const isTopFlopSalesQuantityAvailable = ref<boolean>(true);

const emit = defineEmits<{
  close: [];
  openConfiguration: [];
  confirmExport: [ExportOptionsType];
}>();

onMounted(() => {
  // Avoid autofocus
  nextTick(() => {
    (document.activeElement as HTMLElement)?.blur();
  });

  const exportTopFlopSortKpi = getTopFlopSortKpi(props.dataTableView);

  if (exportTopFlopSortKpi.amount && exportTopFlopSortKpi.quantity === null) {
    exportOptions.value.withTopFlopSalesAmount = true;
    isTopFlopSalesQuantityAvailable.value = false;
  }
  if (exportTopFlopSortKpi.amount === null && exportTopFlopSortKpi.quantity) {
    exportOptions.value.withTopFlopSalesQuantity = true;
    isTopFlopSalesAmountAvailable.value = false;
  }
});

const hasDimensions = computed(() => props.selectedDimensions.length > 0);

const hasDateGranularityDimension = computed(() =>
  DATE_GRANULARITY_DIMENSIONS.some((dimension) => props.selectedDimensions.includes(dimension))
);
const canExport = computed(() => {
  const { exportType, withTopFlopSalesAmount, withTopFlopSalesQuantity } = exportOptions.value;

  if (exportType === 'excel' && isExportWithFormatChoice.value) {
    return false;
  }

  if (exportType === 'excelWithTopFlop' && !withTopFlopSalesAmount && !withTopFlopSalesQuantity) {
    return false;
  }
  return true;
});

const onConfirmExport = () => {
  emit('confirmExport', exportOptions.value);
};

const dimensionsWithExportFormatChoice = ['barcode', 'barcode_anonymized', ...DATE_GRANULARITY_DIMENSIONS];
const isExportWithFormatChoice = computed(() => {
  return props.selectedDimensions.some((dimension) => dimensionsWithExportFormatChoice.includes(dimension));
});
</script>

<template>
  <C4PopUp position="center" :title="t('dataTableExportPopIn.title')" :open="isOpen" @on-close="emit('close')">
    <template #header>
      <div class="p-4 text-xl text-start font-bold">
        {{ t('dataTableExportPopIn.title') }}
      </div>
    </template>
    <template #body>
      <div class="p-4 text-sm w-popup-export">
        <ExportSuccessNotice
          v-if="isExportSucceeded"
          :is-export-csv="exportOptions.exportType === 'csv'"
          :user-country="userCountry"
        />
        <div v-else>
          <p class="text-default whitespace-pre-line py-2">
            {{ t('dataTableExportPopIn.commonText') }}
            <span class="font-bold text-primary cursor-pointer" @click="emit('openConfiguration')">
              {{ t('dataTableExportPopIn.openConfiguration') }}</span
            >
          </p>
          <p class="mt-2 whitespace-pre-line font-bold">
            <span v-if="!hasDimensions">{{ t('dataTableExportPopIn.missingDimensionText') }}</span>
          </p>

          <ExportFormatSelector
            v-if="isExportWithFormatChoice"
            v-model="exportOptions"
            :is-top-flop-sales-amount-available="isTopFlopSalesAmountAvailable"
            :is-top-flop-sales-quantity-available="isTopFlopSalesQuantityAvailable"
          />
          <C4FunctionalNotice
            v-if="hasDateGranularityDimension"
            :closable="false"
            type="warning"
            class="whitespace-pre-line"
            ><template #body>
              <p>{{ t('excelExport.popin.warning.date_granularity') }}</p>
            </template>
          </C4FunctionalNotice>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="p-4 flex gap-4 justify-end">
        <C4ButtonBase
          v-if="isExportSucceeded"
          :label="t('dataTableExportPopIn.successAction')"
          @click="emit('close')"
        />
        <template v-else>
          <C4ButtonBase
            :label="t('dataTableExportPopIn.cancel')"
            variation="secondary"
            :full-width="true"
            @click="emit('close')"
          />
          <C4ButtonBase
            :label="t('dataTableExportPopIn.confirm')"
            :disabled="!hasDimensions || !canExport || isExportPending"
            :is-loading="isExportPending"
            :full-width="true"
            @click="onConfirmExport"
          />
        </template>
      </div>
    </template>
  </C4PopUp>
</template>
